import { Link } from "react-router-dom";
import "@/styles/landing.css";
import "@/styles/theme-lpa.css";
import { LandingProvider, useLanding } from "@/context/LandingContext";
import { SEMINAR } from "@/seminars";
import { Brand } from "@/components/landing/Brand";
import { LanguageSwitch } from "@/components/landing/SiteNav";

const CONTENT = {
  en: {
    back: "← Back to webinar",
    eyebrow: "Affinity Group",
    updated: "Last updated",
    privacy: {
      title: "Privacy Policy",
      intro: `How Affinity Group collects, uses and protects the personal data you provide when registering for the ${SEMINAR.webinar.en}.`,
      sections: [
        { h: "1. Personal data we collect", p: "When you register we collect your full name, email address, mobile number, age range, your chosen webinar session and language, any question you choose to submit, the name of the person who invited you (if provided), and the referral link (if any) through which you reached this page." },
        { h: "2. How we use your data", p: `Your data is used to administer your webinar registration, send confirmation and joining details, contact you about the session, and provide related follow-up information about ${SEMINAR.topic.en}. With your consent, an adviser representative may contact you after the session.` },
        { h: "3. Disclosure", p: "Registration data may be shared with service providers who help us operate this website, send emails and manage registrations (for example email delivery and workflow automation providers). Data is disclosed only to the extent needed to deliver these services." },
        { h: "4. Retention and security", p: "We retain registration data for as long as reasonably required for the purposes above or as required by law. Data is stored securely and access is restricted to authorised personnel." },
        { h: "5. Your rights", p: "Under Singapore's Personal Data Protection Act you may request access to or correction of your personal data, or withdraw consent, by contacting us. Withdrawal of consent may affect our ability to deliver webinar details to you." },
        { h: "6. Contact", p: "For privacy queries, please reply to any registration email you receive from Affinity Group or reach out to your Affinity Group adviser representative." },
      ],
    },
    terms: {
      title: "Terms of Use",
      intro: `Please read these terms before using this website or registering for the ${SEMINAR.webinar.en}.`,
      sections: [
        { h: "1. Educational purpose only", p: "This website and webinar are provided for general information and educational purposes only. Nothing presented constitutes financial, legal, tax, accounting or estate-planning advice, or an offer or recommendation of any financial product." },
        { h: "2. Registration", p: "Registration is complimentary and subject to availability and confirmation. Affinity Group may reschedule or cancel a session and will make reasonable efforts to notify registered attendees." },
        { h: "3. No reliance", p: "Information may not be complete or suitable for your circumstances. You should seek advice from appropriately qualified professionals before making any decision." },
        { h: "4. Intellectual property", p: "All content on this website, including text, design and graphics, belongs to Affinity Group or its licensors and may not be reproduced without permission." },
        { h: "5. About Affinity Group", p: "Affinity Group is a group of financial adviser representatives representing Infinity Financial Advisory Pte. Ltd." },
      ],
    },
  },
  zh: {
    back: "← 返回讲座页面",
    eyebrow: "Affinity Group",
    updated: "最后更新",
    privacy: {
      title: "隐私政策",
      intro: `说明 Affinity Group 如何收集、使用及保护您在报名${SEMINAR.webinar.zh}时所提供的个人资料。`,
      sections: [
        { h: "1. 我们收集的个人资料", p: "报名时，我们会收集您的姓名、电邮地址、手机号码、年龄段、所选讲座场次与语言、您提交的问题（如有）、邀请您参加者的姓名（如有提供），以及您进入本页面所使用的推荐链接（如有）。" },
        { h: "2. 资料的使用方式", p: `您的资料将用于处理讲座报名、发送确认及参加详情、就讲座事宜与您联系，以及提供与${SEMINAR.topic.zh}相关的后续资讯。在您同意的情况下，顾问代表可能会在讲座后与您联系。` },
        { h: "3. 资料披露", p: "报名资料可能会与协助我们运营本网站、发送电邮及管理报名的服务供应商共享（例如电邮发送及流程自动化供应商）。资料仅在提供这些服务所需的范围内披露。" },
        { h: "4. 保存与安全", p: "我们会在上述目的合理所需或法律要求的期限内保存报名资料。资料以安全方式储存，仅限获授权人员访问。" },
        { h: "5. 您的权利", p: "根据新加坡《个人资料保护法》，您可通过联系我们，要求查阅或更正您的个人资料，或撤回同意。撤回同意可能影响我们向您发送讲座详情的能力。" },
        { h: "6. 联系方式", p: "如有隐私相关疑问，请回复您收到的任何 Affinity Group 报名电邮，或联系您的 Affinity Group 顾问代表。" },
      ],
    },
    terms: {
      title: "使用条款",
      intro: `在使用本网站或报名${SEMINAR.webinar.zh}前，请先阅读以下条款。`,
      sections: [
        { h: "1. 仅供教育用途", p: "本网站及讲座仅提供一般资讯及教育用途。所呈现的内容均不构成财务、法律、税务、会计或遗产规划建议，亦不构成任何金融产品的要约或推荐。" },
        { h: "2. 报名", p: "报名免费，并视名额及确认情况而定。Affinity Group 可能重新安排或取消场次，并将尽合理努力通知已报名的参加者。" },
        { h: "3. 不作依赖", p: "资讯可能不完整或不适用于您的个人情况。在作出任何决定前，您应向具备适当资格的专业人士寻求建议。" },
        { h: "4. 知识产权", p: "本网站的所有内容，包括文字、设计及图像，均属 Affinity Group 或其授权方所有，未经许可不得复制。" },
        { h: "5. 关于 Affinity Group", p: "Affinity Group 是代表 Infinity Financial Advisory Pte. Ltd. 的财务顾问代表团队。" },
      ],
    },
  },
};

function LegalContent({ page }) {
  const { lang } = useLanding();
  const dict = CONTENT[lang] || CONTENT.en;
  const content = dict[page];
  return (
    <div className={`landing legal-page theme-${SEMINAR.theme || SEMINAR.id}`} data-testid={`legal-${page}`}>
      <header className="site-nav">
        <div className="container nav-inner">
          <Brand testId="legal-brand" />
          <nav className="nav-links" aria-label="Legal navigation">
            <LanguageSwitch sfx="-legal" />
            <Link className="btn btn-gold nav-mini" to={`/?lang=${lang}`} data-testid="legal-back-btn">{dict.back}</Link>
          </nav>
        </div>
      </header>
      <section className="legal-hero">
        <div className="container">
          <p className="eyebrow">{dict.eyebrow}</p>
          <h1 data-testid="legal-title">{content.title}</h1>
          <p className="lead">{content.intro}</p>
        </div>
      </section>
      <section className="container legal-body">
        {content.sections.map((s) => (
          <div key={s.h}>
            <h2>{s.h}</h2>
            <p>{s.p}</p>
          </div>
        ))}
        <p style={{ marginTop: 40 }}>{dict.updated}: {new Date().getFullYear()}</p>
      </section>
    </div>
  );
}

export default function Legal({ page }) {
  return (
    <LandingProvider>
      <LegalContent page={page} />
    </LandingProvider>
  );
}
